import React from 'react';

import { formatPhone } from '../utils/formatPhone';
import { formatZip } from '../utils/formatZip';
import { isStateDisabled } from '../constants/stateSelectOptions';

export const useValidation = (initialValues, validationRules, t) => {
	const [formData, setFormData] = React.useState(initialValues);
	const [errors, setErrors] = React.useState({});

	React.useEffect(() => {
		setFormData(initialValues);
		setErrors({});
	}, [initialValues]);

	const validateField = (name, value) => {
		if (!validationRules[name]) {
			return '';
		}

		let trimmedValue = value;

		if (typeof value === 'string') {
			trimmedValue = value.trim();
		}

		if (trimmedValue === '') {
			return `${validationRules[name]} ${t('common:required')}`;
		}

		if (
			['termsChecked', 'privacyChecked', 'offerDetailsChecked'].includes(
				name
			) &&
			value !== true
		) {
			return 'NO WAY';
		}

		if (name === 'email') {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

			if (!emailRegex.test(trimmedValue)) {
				return t('common:invalidEmail');
			}
		}

		if (name === 'state' && isStateDisabled(trimmedValue)) {
			return t('common:stateNotEligible');
		}

		if (name === 'cardNumber') {
			const digits = trimmedValue.replace(/\D/g, '');
			if (digits.length < 13) {
				return t('common:invalidCardNumber');
			}
		}

		return '';
	};

	const handleInputChange = (name, value) => {
		let formattedValue = value;

		if (name === 'phone') {
			formattedValue = formatPhone(value);
		}

		if (name === 'zipCode' || name === 'billingZip') {
			formattedValue = formatZip(value);
		}

		if (errors[name]) {
			setErrors((prev) => ({
				...prev,
				[name]: validateField(name, formattedValue)
			}));
		}
		setFormData((prev) => ({ ...prev, [name]: formattedValue }));
	};

	const handleInputBlur = (name, value) => {
		setErrors((prev) => ({
			...prev,
			[name]: validateField(name, value)
		}));
	};

	const validateForm = () => {
		const newErrors = {};
		let valid = true;

		Object.keys(validationRules).forEach((field) => {
			const value = formData[field] ?? '';
			const error = validateField(field, value);

			if (error) {
				newErrors[field] = error;
				valid = false;
			}
		});

		setErrors(newErrors);
		return valid;
	};

	const reset = () => {
		setFormData(initialValues);
		setErrors({});
	};

	return {
		formData,
		errors,
		handleInputChange,
		handleInputBlur,
		validateForm,
		reset
	};
};
